import { useTranslations } from '@vocab/react';
import { Stack, IconDelete, Text, Box } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { type ReactNode } from 'react';

import { linkPropsWithKeyboardNavigation } from '../../../utils/buildHandleEnterAndSpaceKeyPress';
import DeleteConfirmation from '../../common/DeleteConfirmation';
import Loader from '../../common/Loader';

import translations from './.vocab';
import type { ItemType } from './Item';

import * as styles from './ItemContent.css';

interface Props {
  type: ItemType;
  canDelete: boolean;
  onClickDelete: () => void;
  loading: boolean;
  children: ReactNode;
  isConfirmingDelete: boolean;
  onDeleteConfirmed: () => void;
  onDeleteCancelled: () => void;
}

const ItemContent = ({
  children,
  type,
  canDelete,
  loading,
  isConfirmingDelete,
  onClickDelete,
  onDeleteConfirmed,
  onDeleteCancelled,
}: Props) => {
  const { t } = useTranslations(translations);
  if (loading) {
    return (
      <div data-testid="loader">
        <Loader />
      </div>
    );
  }

  if (isConfirmingDelete) {
    return (
      <DeleteConfirmation
        onDelete={onDeleteConfirmed}
        onCancel={onDeleteCancelled}
      >
        {t('Are you sure you want to delete this....', {
          type,
        })}
      </DeleteConfirmation>
    );
  }

  return (
    <>
      <div className={styles.itemContent} data-testid={`${type}-content`}>
        <Stack space="small">
          <div>{children}</div>
        </Stack>
      </div>
      <Box className={styles.deleteItemBinIcon} textAlign="right">
        {canDelete && (
          <Box
            aria-label={t('Delete note')}
            role="button"
            data-testid={`delete-${type}`}
            {...linkPropsWithKeyboardNavigation(onClickDelete)}
          >
            <Text tone="secondary">
              <IconDelete />
            </Text>
          </Box>
        )}
      </Box>
    </>
  );
};

export default observer(ItemContent);
